import type { ContentType, Thread } from "@brookbush/database";
import type { GetThreadsResult } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useCallback, useMemo } from "react";
import { useSWRConfig } from "swr";
import useSWRInfinite, { unstable_serialize } from "swr/infinite";

import fetcher from "~/utils/fetcher.client";

const PAGE_SIZE = 10;
const FIRST_PAGE_KEY = `/threads?limit=${PAGE_SIZE}`;

const useThreads = (enabled: boolean) => {
	const { userId } = useAuth();
	const { mutate: globalMutate } = useSWRConfig();

	const revalidateThreads = useCallback(async () => {
		await globalMutate(
			(key) => typeof key === "string" && key.startsWith("/threads?"),
			undefined,
			{ revalidate: false },
		);
		await Promise.all([
			globalMutate(unstable_serialize(() => FIRST_PAGE_KEY)),
			globalMutate(
				(key) => typeof key === "string" && /\/threads\/\d+/.test(key),
			),
		]);
	}, [globalMutate]);

	const getKey = (
		_pageIndex: number,
		previousPageData: GetThreadsResult | null,
	) => {
		if (!enabled || !userId) return null;
		if (previousPageData && previousPageData.nextCursor === null) return null;

		const cursor = previousPageData?.nextCursor;
		return cursor ? `${FIRST_PAGE_KEY}&cursor=${cursor}` : FIRST_PAGE_KEY;
	};

	const { data, size, setSize, isLoading, isValidating } =
		useSWRInfinite<GetThreadsResult>(
			getKey,
			(url: string) => fetcher<GetThreadsResult>(url, { api: "chat" }),
			{ revalidateFirstPage: false, revalidateOnFocus: false },
		);

	const threads = useMemo(
		() => (data ? data.flatMap((page) => page.threads) : []),
		[data],
	);

	const hasMore = data ? data[data.length - 1]?.nextCursor !== null : false;

	const isLoadingMore =
		isLoading ||
		(size > 0 &&
			!!data &&
			typeof data[size - 1] === "undefined" &&
			isValidating);

	const loadMore = () => setSize(size + 1);

	const createThread = async (input?: {
		title?: string;
		contentType?: ContentType;
		contentId?: string;
	}): Promise<Thread> => {
		const { title = "New conversation", contentType, contentId } = input ?? {};

		const created = await fetcher<Thread>("/threads", {
			api: "chat",
			method: "POST",
			data: {
				title,
				...(contentType && contentId ? { contentType, contentId } : {}),
			},
		});

		await revalidateThreads();
		return created;
	};

	const archiveThread = async (threadId: number): Promise<void> => {
		await fetcher(`/threads/${threadId}`, {
			api: "chat",
			method: "DELETE",
		});

		await revalidateThreads();
	};

	const renameThread = async (
		threadId: number,
		title: string,
	): Promise<void> => {
		await fetcher(`/threads/${threadId}`, {
			api: "chat",
			method: "PATCH",
			data: { title },
		});

		await revalidateThreads();
	};

	return {
		threads,
		hasMore,
		loadMore,
		isLoading,
		isLoadingMore,
		createThread,
		archiveThread,
		renameThread,
		revalidateThreads,
	};
};

export default useThreads;
